import React, { useState, useEffect } from 'react';
import { Info, ChevronDown, ChevronUp, Plus, Trash2, Camera, Check } from 'lucide-react';
import { Button } from '../../../../components/ui';
import { settingsRoomService } from '../../../verve-settings/services/settingsRoomService';

// Custom Accordion Component
const Section = ({ title, expanded, onToggle, children }) => (
  <div className="border border-slate-200 rounded-lg overflow-hidden bg-white mb-2 shadow-sm">
    <div 
      className="px-3 py-2.5 bg-slate-50/80 cursor-pointer flex justify-between items-center transition-colors hover:bg-slate-100/80"
      onClick={onToggle}
    >
      <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">{title}</span>
      {expanded ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
    </div>
    {expanded && (
      <div className="p-3 border-t border-slate-200 space-y-3 bg-white">
        {children}
      </div>
    )}
  </div>
);

const InputField = ({ label, type = "text", value, onChange, placeholder = "" }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] font-semibold text-slate-500 uppercase">{label}</span>
    <input
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full h-8 px-2 text-xs border border-slate-200 rounded-md outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
    />
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] font-semibold text-slate-500 uppercase">{label}</span>
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      className="w-full h-8 px-2 text-xs border border-slate-200 rounded-md outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
    >
      <option value="">Select...</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const CheckboxField = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2 cursor-pointer">
    <input
      type="checkbox"
      checked={checked || false}
      onChange={e => onChange(e.target.checked)}
      className="w-4 h-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500 cursor-pointer"
    />
    <span className="text-xs text-slate-700">{label}</span>
  </label>
);

const COMPONENT_OPTIONS = [
  "Shelves",
  "Drawers",
  "Tandem Box",
  "Wire Basket",
  "Bottle Pullout",
  "Hinges (Pair)",
  "Profile Handle",
  "Legs / Skirting",
  "Cutlery Tray",
  "Wicker Basket",
  "Pull Down Wardrobe Mechanism",
  "Locks"
];

const MeasurementSection = ({ title, expandedSection, toggleSection, data, sectionKey, updateNested, children }) => {
  const updateMeas = (idx, key, value) => {
    const newMeas = [...(data.measurements || [])];
    if (!newMeas[idx]) newMeas[idx] = {};
    newMeas[idx][key] = value;
    updateNested(sectionKey, 'measurements', newMeas);
  };

  const addMeas = () => {
    const newMeas = [...(data.measurements || []), {}];
    updateNested(sectionKey, 'measurements', newMeas);
  };

  const removeMeas = (idx) => {
    const newMeas = [...(data.measurements || [])];
    newMeas.splice(idx, 1);
    updateNested(sectionKey, 'measurements', newMeas);
  };

  const measurements = data.measurements || [{}];
  const lastRow = measurements[measurements.length - 1];
  const canAddRow = measurements.length === 0 || (lastRow && lastRow.zone && lastRow.floor && lastRow.particulars && lastRow.height && lastRow.width && lastRow.depth);

  return (
    <Section title={title} expanded={expandedSection === sectionKey} onToggle={() => toggleSection(sectionKey)}>
      {children}
      
      <div className="flex justify-between items-center border-b pb-1 mb-2 mt-2">
        <h4 className="text-xs font-semibold text-slate-700">Measurements</h4>
        <div className="flex gap-2">
          <Button type="button" onClick={addMeas} disabled={!canAddRow} variant="outline" className={`text-[10px] py-1 h-7 px-3 ${!canAddRow ? 'opacity-50 cursor-not-allowed' : ''}`} size="sm">
            <Plus size={12} className="mr-1" /> Add Row
          </Button>
        </div>
      </div>
      
      <div className="overflow-x-auto pb-2 custom-scrollbar">
        <div className="flex items-center gap-2 px-2 mb-1 min-w-max">
          <div className="w-24 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase">Zone</div>
          <div className="w-20 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase">Floor</div>
          <div className="w-32 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase">Particulars</div>
          <div className="w-14 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase">H(mm)</div>
          <div className="w-14 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase">W(mm)</div>
          <div className="w-14 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase">D(mm)</div>
          <div className="w-16 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase">Qty</div>
          <div className="w-32 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase">Notes</div>
          <div className="w-20 flex-shrink-0 text-[10px] font-semibold text-slate-500 uppercase text-center">Photo</div>
          <div className="w-7 flex-shrink-0"></div>
        </div>
        
        <div className="space-y-1.5 min-w-max">
          {measurements.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 p-1.5 bg-slate-50 border border-slate-100 rounded">
              <div className="w-24 flex-shrink-0">
                <input type="text" placeholder="Zone" value={item.zone || ''} onChange={e => updateMeas(idx, 'zone', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded text-slate-700 focus:outline-none focus:border-slate-800" />
              </div>
              <div className="w-20 flex-shrink-0">
                <input type="text" placeholder="Floor" value={item.floor || ''} onChange={e => updateMeas(idx, 'floor', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded text-slate-700 focus:outline-none focus:border-slate-800" />
              </div>
              <div className="w-32 flex-shrink-0">
                <input type="text" placeholder="Particulars" value={item.particulars || ''} onChange={e => updateMeas(idx, 'particulars', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded text-slate-700 focus:outline-none focus:border-slate-800" />
              </div>
              <div className="w-14 flex-shrink-0">
                <input type="number" placeholder="H" value={item.height || ''} onChange={e => updateMeas(idx, 'height', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded text-slate-700 focus:outline-none focus:border-slate-800" />
              </div>
              <div className="w-14 flex-shrink-0">
                <input type="number" placeholder="W" value={item.width || ''} onChange={e => updateMeas(idx, 'width', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded text-slate-700 focus:outline-none focus:border-slate-800" />
              </div>
              <div className="w-14 flex-shrink-0">
                <input type="number" placeholder="D" value={item.depth || ''} onChange={e => updateMeas(idx, 'depth', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded text-slate-700 focus:outline-none focus:border-slate-800" />
              </div>
              <div className="w-16 flex-shrink-0">
                <input type="text" placeholder="Qty" value={item.qty || ''} onChange={e => updateMeas(idx, 'qty', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded text-slate-700 focus:outline-none focus:border-slate-800" />
              </div>
              <div className="w-32 flex-shrink-0">
                <input type="text" placeholder="Notes" value={item.notes || ''} onChange={e => updateMeas(idx, 'notes', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded text-slate-700 focus:outline-none focus:border-slate-800" />
              </div>
              <div className="w-20 flex-shrink-0">
                <label className="flex items-center justify-center gap-1 cursor-pointer border border-slate-200 hover:bg-slate-50 text-slate-700 w-full py-1.5 rounded h-[28px] bg-white relative overflow-hidden">
                  <Camera size={16} />
                  <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" />
                </label>
              </div>
              <button type="button" onClick={() => removeMeas(idx)} className="h-7 w-7 flex-shrink-0 flex items-center justify-center text-red-500 hover:bg-red-50 rounded transition-colors">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

    </Section>
  );
};

/* 
// commented out by nithi's request
export const SiteVisitWizard = ({ initialData = {}, onSubmit, onCancel, loading = false, lead, fullName, commonNotes }) => {
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem('siteVisitWizardData');
    if (saved) {
      try { return JSON.parse(saved); } catch (e) {}
    }
    return { 
      rooms: [],
      ...initialData 
    };
  });

  const [availableRooms, setAvailableRooms] = useState([]);

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const res = await settingsRoomService.getRooms();
        if (res.success) {
          setAvailableRooms(res.data.filter(r => r.status === 'Active'));
        }
      } catch (error) {
        console.error("Failed to load rooms", error);
      }
    };
    fetchRooms();
  }, []);

  useEffect(() => {
    localStorage.setItem('siteVisitWizardData', JSON.stringify(formData));
  }, [formData]);
  const [expandedSection, setExpandedSection] = useState('rooms');
  const [step, setStep] = useState('edit'); // 'edit' or 'preview'

  const toggleSection = (section) => {
    setExpandedSection(prev => prev === section ? null : section);
  };

  const updateForm = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const updateNested = (section, key, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [key]: value }
    }));
  };

  const handleRoomToggle = (room) => {
    const current = formData.rooms || [];
    if (current.includes(room)) {
      updateForm('rooms', current.filter(r => r !== room));
    } else {
      updateForm('rooms', [...current, room]);
    }
  };

  const updateHardware = (index, key, value) => {
    const newHardware = [...(formData.hardware || [])];
    if (!newHardware[index]) newHardware[index] = {};
    newHardware[index][key] = value;
    updateForm('hardware', newHardware);
  };

  const addHardware = () => {
    updateForm('hardware', [...(formData.hardware || []), {}]);
  };

  const removeHardware = (index) => {
    const newHardware = [...(formData.hardware || [])];
    newHardware.splice(index, 1);
    updateForm('hardware', newHardware);
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    localStorage.removeItem('siteVisitWizardData');
    onSubmit(formData);
  };

  const APPLIANCES = ["Chimney", "Hob", "Microwave", "Refrigerator", "Water Purifier", "Oven"];

  if (step === 'preview') {
    return (
      <div className="flex flex-col gap-5 p-1">
        <div className="space-y-4 h-[450px] overflow-y-auto pr-2 custom-scrollbar">
          <h3 className="text-[12px] font-bold text-slate-800 uppercase tracking-widest mb-3 border-b border-slate-100 pb-2">
            Preview Site Visit Details
          </h3>
          
          <div className="space-y-4">
            <div>
              <h4 className="text-[10px] font-bold text-slate-500 uppercase">Selected Rooms</h4>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {formData.rooms?.length > 0 ? formData.rooms.map(room => (
                  <span key={room} className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-[11px] font-semibold">{room}</span>
                )) : <span className="text-xs text-slate-400">No rooms selected</span>}
              </div>
            </div>

            {formData.rooms?.map(room => {
              const measurements = formData[room]?.measurements || [];
              if (measurements.length === 0 || Object.keys(measurements[0]).length === 0) return null;
              
              return (
                <div key={room} className="border border-slate-200 rounded-md p-3 bg-slate-50/50">
                  <h4 className="text-[11px] font-bold text-slate-700 uppercase mb-2">{room} Measurements ({measurements.length})</h4>
                  <div className="space-y-2">
                    {measurements.map((m, idx) => (
                      <div key={idx} className="text-[11px] text-slate-600 grid grid-cols-2 sm:grid-cols-4 gap-2 bg-white p-2 border border-slate-100 rounded">
                        <div><span className="font-semibold">Zone:</span> {m.zone || '-'}</div>
                        <div><span className="font-semibold">Particulars:</span> {m.particulars || '-'}</div>
                        <div><span className="font-semibold">Dimensions:</span> {m.height||'-'}x{m.width||'-'}x{m.depth||'-'}</div>
                        <div><span className="font-semibold">Qty:</span> {m.qty || '-'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {commonNotes && (
              <div>
                <h4 className="text-[10px] font-bold text-slate-500 uppercase mt-2">Notes</h4>
                <div className="mt-1 text-xs text-slate-700 bg-slate-50 p-2 rounded border border-slate-100">
                  Preview of notes will appear here...
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-3 border-t border-slate-100 mt-2">
          <button
            type="button"
            onClick={() => setStep('edit')}
            className="w-full sm:flex-1 cursor-pointer py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-800 rounded-md text-[12px] font-bold transition-all duration-300 shadow-sm"
          >
            Back to Edit
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="w-full sm:flex-1 cursor-pointer py-2.5 min-h-[38px] bg-slate-900 hover:bg-black text-white rounded-md text-[12px] font-bold transition-all duration-300 ease-in-out shadow-sm flex items-center justify-center gap-2"
          >
            {loading ? "Processing..." : "Confirm & Submit"}
            {!loading && <Check size={14} />}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 p-1">
      <div className="space-y-4 h-[450px] overflow-y-auto pr-2 custom-scrollbar">
        <div className="mb-3">
          <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 border-b border-slate-100 pb-2">
            01. SELECT ROOMS REQUIRED
          </h3>
          <div className="flex flex-wrap gap-2 mt-2">
            {availableRooms.map(roomObj => {
              const room = roomObj.name;
              const isSelected = formData.rooms?.includes(room);
              return (
                <button
                  key={room}
                  type="button"
                  onClick={() => {
                    handleRoomToggle(room);
                    if (!isSelected) {
                      setExpandedSection(room);
                    }
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold transition-all duration-200 border shadow-sm ${
                    isSelected 
                      ? 'bg-slate-900 border-slate-900 text-white' 
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {isSelected ? <Check size={14} className="text-white" /> : <Plus size={14} className="text-slate-400" />}
                  {room}
                </button>
              );
            })}
          </div>
        </div>

        {(formData.rooms || []).map(selectedRoomName => (
          <MeasurementSection
            key={selectedRoomName}
            title={`${selectedRoomName} Measurement Form`}
            expandedSection={expandedSection}
            toggleSection={toggleSection}
            data={formData[selectedRoomName] || {}}
            sectionKey={selectedRoomName}
            updateNested={updateNested}
          />
        ))}

        {commonNotes}
      </div>

      <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-3 border-t border-slate-100 mt-2">
        <button
          type="button"
          onClick={() => {
            localStorage.removeItem('siteVisitWizardData');
            onCancel();
          }}
          className="w-full sm:flex-1 cursor-pointer py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-800 rounded-md text-[12px] font-bold transition-all duration-300 shadow-sm"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => setStep('preview')}
          className="w-full sm:flex-1 cursor-pointer py-2.5 min-h-[38px] bg-slate-900 hover:bg-black text-white rounded-md text-[12px] font-bold transition-all duration-300 ease-in-out shadow-sm border border-slate-900 flex items-center justify-center gap-2"
        >
          Preview Details
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
        </button>
      </div>
    </div>
  );
};
*/
export { SiteVisitWizard } from '../../../client_contact_stages/Site-Visit/components/SiteVisitWizard';
